'use client';

import { createIntl } from '@formatjs/intl';
import type { userAgent } from 'next/server';
import { PropsWithChildren, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import ScreenLockProvider from '@alltrails/core/components/ScreenLockProvider';
import { ListsState } from '@alltrails/lists/slices/lists';
import MapProvider from '@alltrails/maps/components/MapProvider/MapProvider';
import { CustomRouteState } from '@alltrails/maps/redux/customRoute';
import type { MapState } from '@alltrails/maps/redux/reducer';
import type { Bootstrap } from '@alltrails/networking/types/Bootstrap';
import type CountryCode from '@alltrails/types/CountryCode';
import type Experiments from '@alltrails/types/Experiments';
import { setupListeners } from '@/utils/listenerMiddleware';
import { buildStore } from '../utils/redux';

type Props = {
  bootstrap: Bootstrap;
  browser: ReturnType<typeof userAgent>['browser'];
  experiments: Experiments;
  irclickid?: string;
  lists: ListsState;
  locale: ReturnType<typeof createIntl>['locale'];
  map: MapState;
  customRoute: CustomRouteState;
  messages: ReturnType<typeof createIntl>['messages'];
  requestCountryCode: CountryCode;
};

setupListeners();

export default function CustomProvider({
  bootstrap,
  browser,
  experiments,
  lists,
  map,
  customRoute,
  messages,
  locale,
  children,
  irclickid,
  requestCountryCode
}: PropsWithChildren<Props>) {
  // Need to memoize the store so that we can change the url params when the map moves without resetting the store each time
  const store = useMemo(
    () =>
      buildStore({
        algoliaConfigs: bootstrap.algoliaConfig,
        amplitudeAnalyticsSettings: bootstrap.amplitudeAnalyticsSettings,
        browser,
        context: bootstrap.context,
        currencyCode: bootstrap.currencyCode,
        experiments,
        features: bootstrap.features,
        irclickid,
        lists,
        map,
        customRoute,
        plans: bootstrap.plans,
        requestCountryCode
      }),
    [
      bootstrap.algoliaConfig,
      bootstrap.amplitudeAnalyticsSettings,
      bootstrap.context,
      bootstrap.currencyCode,
      bootstrap.features,
      bootstrap.plans,
      browser,
      customRoute,
      experiments,
      irclickid,
      lists,
      map,
      requestCountryCode
    ]
  );

  return (
    <IntlProvider messages={messages} locale={locale}>
      <Provider store={store}>
        <MapProvider>
          <ScreenLockProvider hidePageScrollbarGutter>{children}</ScreenLockProvider>
        </MapProvider>
      </Provider>
    </IntlProvider>
  );
}
